import openSocket from 'socket.io-client';
import {
	updateConvo,
	updateThreads,
	updateContact,
	updateName,
	threadUpdate,
	convoUpdate,
	receiveMsg,
	sendLimitReached
} from './reducers/inbox/actions';
import {
	updateNotification
} from './reducers/notification/actions';
import { push } from 'react-router-redux';
import {useStore} from './components/inbox-v2/inbox-store';
import useContactProfileStore from './stores/useContactProfileStore';
import useContactsStore from './stores/useContactsStore';
import useGroupsStore from './stores/useGroupsStore';
import useGroupDetailStore from './stores/useGroupDetailStore';
import useProfileStore from './stores/useProfileStore';
import session from './utils/session';

import _ from 'lodash';

// Default to a no-op so handlers can call Dispatch unconditionally before the
// store is registered. setReduxStore() in index.js wires the real dispatch at
// app boot — required because some initRoom call sites (e.g. auth-helpers)
// pass history instead of dispatch, which would otherwise leave Dispatch as a
// non-function object and silently break the bell-icon real-time updates.
let Dispatch = () => {};

export const setReduxStore = (store) => {
	Dispatch = store.dispatch;
};

const socket = openSocket(process.env.REACT_APP_API || 'http://localhost:8787', {
	transports: ['websocket'],
});

// Track joined rooms so they can be re-joined after reconnection
const joinedRooms = new Set();

socket.on('connect', () => {
	console.log('[io.js] socket connected, id:', socket.id);
	// Re-join all rooms after reconnection (rooms are server-side state lost on disconnect)
	if (joinedRooms.size > 0) {
		console.log('[io.js] re-joining rooms:', [...joinedRooms]);
		joinedRooms.forEach(room => socket.emit('room', room));
	}
	// Company-wide fan-outs (contact tag/group/star updates) are emitted to the
	// companyId room as well as the contactId room. Without joining it, only the
	// contact you currently have open receives updates — which left the tag
	// filter options stale on the inbox list and the Contacts page. The
	// extension's background worker already joins this room.
	syncCompanyRoom();
});
socket.on('disconnect', (reason) => console.log('[io.js] socket disconnected:', reason));
socket.on('error', (err) => console.error('[io.js] socket error:', err));

const connectTo = room => { joinedRooms.add(room); socket.emit('room', room); };

socket.on('sendMsgLimitReached', () => { try { if (typeof Dispatch === 'function') Dispatch(sendLimitReached({ sendLimitReached: true })); } catch (e) { console.error('sendMsgLimitReached handler error:', e); } });

socket.on('fetchThreads', thread => {
	try {
		// Update Zustand inbox store — fetchThreads arrives via conversation_id room,
		// so it's always for the active contact's conversation if the room was joined.
		const store = useStore.getState();
		const { activeContact, threads, currentConversationRoom } = store;
		if (thread && thread.id && activeContact && currentConversationRoom) {
			// Verify this thread belongs to the joined conversation room
			if (currentConversationRoom === thread.conversation_id) {
				const exists = threads.some(t => t.id === thread.id);
				if (!exists) {
					store.updateConversationWithNewMessage(activeContact, thread, 'inbound');
					// Insert sorted by created_at — scheduled bubbles carry future
					// dates, so a plain push would land just-now messages after the
					// future-dated reminder bubble. Normalize via getTime() since
					// the array mixes Date objects (from REST) and ISO strings
					// (from socket payloads), which `_.sortBy` doesn't compare cleanly.
					const next = _.sortBy([...threads, thread], (t) => new Date(t.created_at).getTime());
					useStore.setState({ threads: next });
				}
			}
		}
		if (typeof Dispatch === 'function') Dispatch(updateThreads(thread));
	} catch (e) { console.error('fetchThreads handler error:', e); }
});

socket.on('fetchConvo', convo => { try { if (typeof Dispatch === 'function') Dispatch(updateConvo(convo)); } catch (e) { console.error('fetchConvo handler error:', e); } });

socket.on('fetchContact', contact => { try { if (typeof Dispatch === 'function') Dispatch(updateContact(contact)); } catch (e) { console.error('fetchContact handler error:', e); } });

socket.on('fetchName', data => { try { if (typeof Dispatch === 'function') Dispatch(updateName(data)); } catch (e) { console.error('fetchName handler error:', e); } });

socket.on('threadUpdate', thread => {
	console.log("socket:onThreadUpdate", thread);

	const store = useStore.getState();
	const { conversations } = store;

	// Check if the thread update is for a contact in the conversations list
	const contactExists = _.find(conversations, { contact_id: thread.conversation.contact_id });

	if (contactExists) {
		// Update the conversation's message preview and move to top
		store.updateConversationWithNewMessage(thread.conversation.contact_id, {
			message: thread.message,
			created_at: thread.created_at
		}, thread.direction || 'outbound');
	}

	// Let pushThread check activeContact via get() for the freshest state
	store.pushThread(thread, 'threadUpdate');

	// Handle scheduled messages if present
	if (thread.scheduledMessages && thread.scheduledMessages.length > 0) {
		thread.scheduledMessages.forEach(scheduledMsg => {
			store.pushThread(scheduledMsg, 'scheduledMessage');
		});
	}

	// Dispatch Redux action for backward compatibility
	if (typeof Dispatch === 'function') Dispatch(threadUpdate(thread));
});

// ADR-027: scheduled-message bubble events
socket.on('scheduled_message:cancelled', (payload) => {
	try {
		useStore.getState().applyScheduledMessageSocketEvent('scheduled_message:cancelled', payload);
	} catch (e) { console.error('scheduled_message:cancelled handler error:', e); }
});

socket.on('scheduled_message:sent', (payload) => {
	try {
		useStore.getState().applyScheduledMessageSocketEvent('scheduled_message:sent', payload);
	} catch (e) { console.error('scheduled_message:sent handler error:', e); }
});

socket.on('scheduled_message:rescheduled', (payload) => {
	try {
		useStore.getState().applyScheduledMessageSocketEvent('scheduled_message:rescheduled', payload);
	} catch (e) { console.error('scheduled_message:rescheduled handler error:', e); }
});

socket.on('scheduled_message:deleted', (payload) => {
	try {
		useStore.getState().applyScheduledMessageSocketEvent('scheduled_message:deleted', payload);
	} catch (e) { console.error('scheduled_message:deleted handler error:', e); }
});

socket.on('scheduled_message:created', (payload) => {
	try {
		useStore.getState().applyScheduledMessageSocketEvent('scheduled_message:created', payload);
	} catch (e) { console.error('scheduled_message:created handler error:', e); }
});

socket.on('convoUpdate', contact => {
	// Update Zustand conversation list if latest message data is included
	if (contact && contact.id && contact._latestMessage) {
		const store = useStore.getState();
		const { conversations } = store;
		const contactExists = _.find(conversations, { contact_id: contact.id });
		if (contactExists) {
			store.updateConversationWithNewMessage(contact.id, {
				message: contact._latestMessage,
				created_at: contact._latestCreatedAt
			}, contact._latestDirection || 'outbound');
		}
	}
	if (typeof Dispatch === 'function') Dispatch(convoUpdate(contact));
});

socket.on('receiveMsg', (data) => {
	console.log("socket:receiveMsg", data);

	const store = useStore.getState();
	const { conversations } = store;

	// Check if the message is for a contact in the conversations list
	const contactExists = _.find(conversations, { contact_id: data.contact.id });

	if (contactExists) {
		// Update the conversation's message preview and move to top
		store.updateConversationWithNewMessage(data.contact.id, data.thread, 'inbound');
	}

	// Let pushThread check activeContact via get() for the freshest state
	store.pushThread(data);

	// Dispatch Redux action for backward compatibility
	if (typeof Dispatch === 'function') Dispatch(receiveMsg(data));
});

socket.on('showNotif', notif => {
	const closeNotif = n => {
			n.close();
			window.focus();
			window.location.reload();
	};
	const displayNotif = message => {
		let notification = new Notification(message);

		notification.onclick = event => {
			// prevent the browser from focusing the Notification's tab
			event.preventDefault();
			if (_.isFunction(Dispatch)) {
				Dispatch(push(`/contacts/${notif.contactId}`));
				return closeNotif(notification);
			}
			// Dispatch.push(`/contacts/${notif.contactId}`); // This looked wrong in original code too, removed logic or safe guarded
			if (typeof Dispatch === 'function') Dispatch(push(`/contacts/${notif.contactId}`));
			closeNotif(notification);
		}
	};

	if (notif.isErr) {
		return displayNotif('Applicant Stuck up! Needs a human');
	}
	displayNotif('Applicant successfully filed up.');
});

// Contact tag / group fan-out (cross-user real-time updates).
// One backend mutation can emit to companyId + contactId rooms; Socket.io
// dedupes per socket, so each store action runs once per client per event.
socket.on('contact:tags-updated', (payload) => {
	try {
		if (!payload || !payload.contactId) return;
		useStore.getState().applyContactTagsUpdated(payload);
		useContactProfileStore.getState().applyContactTagsUpdated(payload);
		useContactsStore.getState().applyContactTagsUpdated(payload);
	} catch (e) { console.error('contact:tags-updated handler error:', e); }
});

socket.on('contact:groups-updated', (payload) => {
	try {
		if (!payload || !payload.contactId) return;
		useStore.getState().applyContactGroupsUpdated(payload);
		useContactProfileStore.getState().applyContactGroupsUpdated(payload);
		useContactsStore.getState().applyContactGroupsUpdated(payload);
	} catch (e) { console.error('contact:groups-updated handler error:', e); }
});

socket.on('group:members-changed', (payload) => {
	try {
		if (!payload || !payload.groupId) return;
		useGroupsStore.getState().applyGroupMembersChanged(payload);
		useGroupDetailStore.getState().applyGroupMembersChanged(payload);
	} catch (e) { console.error('group:members-changed handler error:', e); }
});

socket.on('contact:star-updated', (payload) => {
	try {
		if (!payload || !payload.contactId) return;
		useStore.getState().applyContactStarUpdated(payload);
		useContactProfileStore.getState().applyContactStarUpdated(payload);
		useContactsStore.getState().applyContactStarUpdated(payload);
	} catch (e) { console.error('contact:star-updated handler error:', e); }
});

// AI translation (ADR-035). A translation landed on an existing message row
// (inbound detection runs after the receiveMsg fan-out; on-demand too) — emitted
// to the contact + conversation rooms, so only the open conversation hears it.
// Fan out to both thread stores: inbox-v2 and the /contacts/:id page
// (useContactProfileStore), which renders the same ThreadItem from its own list.
socket.on('thread:translated', (payload) => {
	try {
		if (!payload || !payload.threadId) return;
		useStore.getState().applyThreadTranslation(payload);
		useContactProfileStore.getState().applyThreadTranslation(payload);
	} catch (e) { console.error('thread:translated handler error:', e); }
});

socket.on('contact:language-updated', (payload) => {
	try {
		if (!payload || !payload.contactId) return;
		useStore.getState().applyContactLanguageUpdated(payload);
		useContactProfileStore.getState().applyContactLanguageUpdated(payload);
	} catch (e) { console.error('contact:language-updated handler error:', e); }
});

// Per-user inbox Archive (ADR-034). Emitted to this user's room only; the
// inbox store decides whether the row leaves or (re)enters the current view.
socket.on('contact:archive-updated', (payload) => {
	try {
		if (!payload || !payload.contactId) return;
		useStore.getState().applyContactArchiveUpdated(payload);
	} catch (e) { console.error('contact:archive-updated handler error:', e); }
});

// Template + sequence library (inbox-v2 composer): re-uses the same
// companyId+userId fan-out as the tag/star events. Inbox-v2 has joined both
// rooms by this point; Socket.io dedupes per socket so the apply* call runs
// exactly once per event per client.
socket.on('template:created', (payload) => {
	try {
		if (!payload || !payload.template) return;
		useStore.getState().applyTemplateCreated(payload.template);
	} catch (e) { console.error('template:created handler error:', e); }
});

socket.on('template:updated', (payload) => {
	try {
		if (!payload || !payload.template) return;
		useStore.getState().applyTemplateUpdated(payload.template);
	} catch (e) { console.error('template:updated handler error:', e); }
});

socket.on('template:deleted', (payload) => {
	try {
		if (!payload || !payload.templateId) return;
		useStore.getState().applyTemplateDeleted(payload.templateId);
	} catch (e) { console.error('template:deleted handler error:', e); }
});

socket.on('sequence:created', (payload) => {
	try {
		if (!payload || !payload.sequence) return;
		useStore.getState().applySequenceCreated(payload.sequence);
	} catch (e) { console.error('sequence:created handler error:', e); }
});

socket.on('sequence:updated', (payload) => {
	try {
		if (!payload || !payload.sequence) return;
		useStore.getState().applySequenceUpdated(payload.sequence);
	} catch (e) { console.error('sequence:updated handler error:', e); }
});

socket.on('sequence:deleted', (payload) => {
	try {
		if (!payload || !payload.sequenceId) return;
		useStore.getState().applySequenceDeleted(payload.sequenceId);
	} catch (e) { console.error('sequence:deleted handler error:', e); }
});

socket.on('user:avatar-updated', (payload) => {
	try {
		if (!payload || !payload.userId) return;
		const userData = session.get('userData');
		if (userData && userData.id === payload.userId) {
			// Mirror useProfileStore.uploadUserAvatar / selectAvatarPreset:
			// keep session + store + layout header (via avatarUpdated custom event) in sync.
			session.set('userData', {
				...userData,
				avatar_url: payload.avatar_url,
				avatar_type: payload.avatar_type,
			});
			useProfileStore.setState({
				avatarUrl: payload.avatar_url || '',
				avatarType: payload.avatar_type || 'preset',
			});
			window.dispatchEvent(new CustomEvent('avatarUpdated', { detail: payload.avatar_url }));
		}
		useStore.getState().applyUserAvatarUpdated(payload);
		useContactProfileStore.getState().applyUserAvatarUpdated(payload);
	} catch (e) { console.error('user:avatar-updated handler error:', e); }
});

socket.on('displayNotif', notification => {
	if (typeof Dispatch === 'function') Dispatch(updateNotification(notification));

	// Sibling/parent fan-out: the contact lives in a different account's inbox,
	// so it won't be in the current user's `conversations` store. Bell list is
	// still updated above; we just skip the tile-mark-unread side effect.
	if (notification && notification.viaUserId) return;

	// Mark the conversation tile as unread so the green dot appears immediately
	const contactId = notification?.notification?.contact_id;
	if (contactId) {
		const store = useStore.getState();
		const { conversations, activeContact } = store;
		// Don't mark as unread if the user is already viewing this contact
		if (contactId !== activeContact) {
			const idx = _.findIndex(conversations, { contact_id: contactId });
			if (idx !== -1 && !conversations[idx].hasUnreadNotification) {
				const updated = [...conversations];
				updated[idx] = { ...updated[idx], hasUnreadNotification: true };
				useStore.setState({ conversations: updated });
			}
		}
	}
});

const initRoom = (room) => {
	connectTo(room);
};

const leaveRoom = room => { joinedRooms.delete(room); socket.emit('unsubscribe', room); };

const joinRoom = room => { joinedRooms.add(room); socket.emit('room', room); };

// Tracks which company room we're in so a company switch can swap rooms rather
// than accumulate them. Call with no argument to use the session's company.
let joinedCompanyRoom = null;
const syncCompanyRoom = (companyId) => {
	const cid = companyId || session.get('userData')?.companyId;
	if (!cid || cid === joinedCompanyRoom) return;
	if (joinedCompanyRoom) leaveRoom(joinedCompanyRoom);
	joinedCompanyRoom = cid;
	joinRoom(cid);
};

export {
	initRoom,
	leaveRoom,
	joinRoom,
	syncCompanyRoom,
	socket,
}
